import React, { useCallback, useContext, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Indicator,
  Menu,
  Paper,
  Text,
  Title
} from "@mantine/core";
import { IconDotsVertical, IconEdit, IconTrash, IconX } from "@tabler/icons-react";
import { capitalize } from "inflection";
import Can, { AbilityContext } from "./Can";
import RequestIcon from "./RequestIcon";
import RequestStatusBadge from "./RequestStatusBadge";
import Contact from "./contacts/Contact";
import RequestAttachments from "./RequestAttachments";
import {
  BusinessCard,
  DesignProject,
  Event,
  MarketingCampaign,
  SocialMediaPost,
  WebUpdate
} from "../components/request-types";
import {
  RequestBaseFields,
  BusinessCardFields,
  DesignProjectFields,
  EventFields,
  MarketingCampaignFields,
  SocialMediaPostFields,
  WebUpdateFields,
  BaseRequestFields
} from "../types/request.types";
import { formatDateTime } from "../lib/date-time-formatters";
import { ContactFields } from "../types/contact.types";
import { useWasInViewport } from "../hooks/use-was-in-viewport";
import Comments from "./Comments";
import CommentForm from "./CommentForm";
import Assignment from "./Assignment";
import { UserFields } from "../types/user.types";
import FormContacts from "./contacts/FormContacts";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { notifications } from "@mantine/notifications";
import { requestResourceUrl } from "../lib/mts-url";
import csrfToken from "../lib/csrf-token";

const requestComponents = {
  'business_card_request': BusinessCard,
  'design_project_request': DesignProject,
  'event_request': Event,
  'marketing_campaign_request': MarketingCampaign,
  'social_media_post_request': SocialMediaPost,
  'web_update_request': WebUpdate,
};

interface RequestBodyProps {
  request:
    RequestBaseFields &
    BusinessCardFields &
    DesignProjectFields &
    EventFields &
    MarketingCampaignFields &
    SocialMediaPostFields &
    WebUpdateFields;
  onClose: Function;
}

interface RequestSectionTitleProps {
  children: React.ReactNode;
}

const RequestSectionTitle = ({ children }: RequestSectionTitleProps) => (
  <Title order={2} mb="lg" size="1.25rem" fw={700}>{children}</Title>
);

export default function RequestBody({ request, onClose }: RequestBodyProps) {
  const [commentFormKey, setCommentFormKey] = useState(0);
  const contentTitle = useRef<string | null>(null);
  const attachmentsInt = useWasInViewport();
  const commentsInt = useWasInViewport();
  const ReqComponent = requestComponents[request.request_type];
  const ability = useContext(AbilityContext);
  const [editingContacts, handlers] = useDisclosure(false);
  const menuItems = [];

  const contactsForm = useForm<BaseRequestFields>({
    initialValues: {
      name: '',
      combined_contacts: request.combined_contacts,
    },
  });

  const queryClient = useQueryClient();
  const saveContactsMutation = useMutation({
    mutationFn: async (contactsData: BaseRequestFields) => {
      const url = requestResourceUrl(request, "contacts");
      return axios.patch(url, {
        combined_contacts: contactsData.combined_contacts,
      }, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken() },
      });
    },
    onSuccess: () => {
      notifications.show({
        message: "Contacts updated successfully.",
        color: "teal",
      });
      queryClient.invalidateQueries();
      handlers.close();
    },
    onError: () => {
      notifications.show({
        message: "Failed to update contacts.",
        color: "red",
      });
    },
  });

  if (contentTitle.current === null) {
    contentTitle.current = capitalize(
      request.request_type.split('_').slice(0, -1).join(' ')
    );
  }

  if (ability.can('delete', 'Request')) {
    menuItems.push(
      <Menu.Item key="request-delete" leftSection={<IconTrash />}>
        Delete
      </Menu.Item>
    );
  }

  const onCommentSubmit = useCallback(() => (
    setCommentFormKey(prevKey => prevKey + 1)
  ), []);

  return (
    <article>
      <Paper
        withBorder
        radius={0}
        component="header"
        mb="md"
        bg="white"
        m="calc(-1 * var(--mantine-spacing-xl))"
        px="lg"
        py="xs"
      >
        <Group>
          <Group style={{flexGrow: 1}}>
            <ActionIcon
              onClick={() => onClose && onClose()}
              variant="white"
              size="lg"
              color="gray.9"
            >
              <IconX width="100%" height="100%" />
            </ActionIcon>
            <Box>
              <Group gap="sm">
                <RequestIcon requestType={request.request_type} size={34} stroke={1.25} />
                <Title order={1} my="sm">
                  {contentTitle.current}
                </Title>
              </Group>
              <Group gap="sm">
                <RequestStatusBadge status={request.status} />
                <Text size="sm" c="gray.7">
                  Submitted <time dateTime={request.created_at}>
                    {formatDateTime(request.created_at)}
                  </time>
                </Text>
              </Group>
              <Can do="update_assignment" on="Request">
                <Box mt="xs">
                  <Assignment request={request} />
                </Box>
              </Can>
            </Box>
          </Group>
          {menuItems.length > 0 &&
            <Menu>
              <Menu.Target>
                <ActionIcon variant="default" size="lg">
                  <IconDotsVertical />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>{menuItems}</Menu.Dropdown>
            </Menu>
          }
        </Group>
      </Paper>

      <Paper component="section" my="md" p="md" withBorder>
        <Group>
          <RequestSectionTitle>Contacts</RequestSectionTitle>
          <Can do="manage" on="Contact">
            <ActionIcon
              variant="transparent"
              c="gray.9"
              size="sm"
              pt={0}
              mt={0}
              style={{alignSelf: "start"}}
              disabled={editingContacts}
              onClick={() => handlers.open()}
            >
              <IconEdit />
            </ActionIcon>
          </Can>
        </Group>
        {editingContacts ? (
          <>
            <FormContacts<BaseRequestFields>
              form={contactsForm}
              request={request}
            />
            <Group justify="end" mt="lg">
              <Button
                variant="light"
                onClick={() => {
                  handlers.close();
                  contactsForm.reset();
                }}
              >
                Cancel
              </Button>
              <Button
                loading={saveContactsMutation.isPending}
                onClick={() => saveContactsMutation.mutate(contactsForm.values)}
              >
                Save Contacts
              </Button>
            </Group>
          </>
        ) : (
          <Group align="stretch">
            <Indicator
              label="Submitter"
              size={16}
              position="top-center"
              color="gray"
            >
              <Contact contact={request.user} />
            </Indicator>
            {request.combined_contacts.map((c: (UserFields | ContactFields)) => (
              <Contact key={`${c.contact_type}-${c.id}`} contact={c} />
            ))}
          </Group>
        )}
      </Paper>
      <Paper component="section" my="md" p="md" withBorder>
        <RequestSectionTitle>Details</RequestSectionTitle>
        <ReqComponent request={request} />
      </Paper>
      <Paper component="section" my="md" p="md" ref={attachmentsInt.ref} withBorder>
        <RequestSectionTitle>Attachments</RequestSectionTitle>
        {attachmentsInt.wasInViewport && <RequestAttachments request={request} />}
      </Paper>
      <Paper component="section" my="md" p="md" ref={commentsInt.ref} withBorder>
        <RequestSectionTitle>Comments</RequestSectionTitle>
        {commentsInt.wasInViewport && <Comments request={request} />}
        <Box mt="md">
          <CommentForm key={commentFormKey} onSubmit={onCommentSubmit} request={request} />
        </Box>
      </Paper>
    </article>
  )
}
